import { atom } from 'jotai';
import { getLocalDateIso } from '../config/timezone';

/**
 * Richiesta di gestire una non conformità con l'assistente AI, scritta dalla
 * sezione "Azione correttiva richiesta" sulla card e consumata dalla route
 * shell dei self-check (`SelfCheckAssistantShell`), che apre il pannello e
 * passa il messaggio all'invio automatico.
 *
 * `message` parte in italiano (la lingua dell'agente NC): nomina l'entità e
 * il giorno così il tool di compliance individua la NC aperta giusta anche
 * quando sulla pagina ce ne sono più di una. `nonce` rende unico il
 * richiamo: due click su due NC diverse (o ripetuti sulla stessa) devono
 * sempre riaprire e riseminare la chat.
 */
export interface AssistantNcRequest {
  message: string;
  nonce: number;
}

export const assistantNcRequestAtom = atom<AssistantNcRequest | null>(null);

/** Messaggio di apertura per la gestione assistita di una NC.」（in italiano:
 * l'intervista NC dell'assistente è servita in italiano, come gli starter
 * chip del pannello).
 *
 * `recordDate` viene sempre fatto passare per `getLocalDateIso()` prima di
 * finire nel messaggio: un valore già `YYYY-MM-DD` esce invariato, ma un
 * instant ISO completo (es. `recordedAt` di un record temperatura) viene
 * risolto al giorno di calendario Europe/Rome — lo stesso che il backend
 * assegna con `BusinessDay.fromInstant`. Senza questa normalizzazione un
 * record delle 22:10 UTC di settembre (00:10 CEST del giorno dopo) finiva nel
 * messaggio come "del 2026-09-16T22:10:14.536Z", mentre il PDF firmato e il
 * tool di compliance lo registravano sotto il 17 — un disallineamento tra
 * quello che l'assistente dice e quello che il registro dice. */
const ITALIAN_LONG_DATE_FORMAT = new Intl.DateTimeFormat('it-IT', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

/** `YYYY-MM-DD` → "del 29 settembre 2026", "dell'8 settembre 2026",
 * "del 1° marzo 2026". Formatted in UTC so the calendar day never shifts:
 * the input is already the Europe/Rome business day. */
function formatItalianDateWithPreposition(localDateIso: string): string {
  const [year, month, day] = localDateIso.split('-').map(Number);
  const formattedDate = ITALIAN_LONG_DATE_FORMAT.format(new Date(Date.UTC(year, month - 1, day)));
  if (day === 1) {
    return `del ${formattedDate.replace(/^1 /, '1° ')}`;
  }
  if (day === 8 || day === 11) {
    return `dell'${formattedDate}`;
  }
  return `del ${formattedDate}`;
}

export function buildAssistantNcMessage(
  entityName: string | undefined,
  recordDate: string,
  recordId?: string,
): string {
  const localRecordDate = formatItalianDateWithPreposition(getLocalDateIso(recordDate));
  const request = entityName
    ? `Voglio gestire la non conformità di "${entityName}" ${localRecordDate}: avvia subito la procedura guidata.`
    : `Voglio gestire la non conformità ${localRecordDate}: avvia subito la procedura guidata.`;
  // Entity + day cannot tell two open NCs of the same equipment on the same
  // day apart: the hidden `[nc: <recordId>]` marker binds the server-side
  // interview to exactly this record.
  return recordId ? `${request}\n[nc: ${recordId}]` : request;
}
